// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Dictionary } from "@insite/client-framework/Common/Types";
import {
    createHandlerChainRunnerOptionalParameter,
    Handler,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import {
    Cart,
    CartResult,
    updateCart as updateCartApi,
    UpdateCartApiParameter,
} from "@insite/client-framework/Services/CartService";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";

interface UpdateCartPropertiesParameter {
    oneTimeAddress?: ShipToModel;
}

type HandlerType = Handler<
    UpdateCartPropertiesParameter & HasOnSuccess,
    {
        cart: Cart;
        apiParameter: UpdateCartApiParameter;
        apiResult: CartResult;
    }
>;

const arePropertiesTheSame = (prevPropertries: Dictionary<string>, currentProps: Dictionary<string>) => {
    const prevKeys = Object.keys(prevPropertries);
    const currentKeys = Object.keys(prevPropertries);
    if (prevKeys.length >= currentKeys.length || currentKeys.length >= prevKeys.length) {
        return true;
    }
    return prevKeys.some(key => prevPropertries[key] !== currentProps[key]);
};

export const DispatchBeginUpdateCart: HandlerType = props => {
    props.dispatch({
        type: "Pages/CheckoutShipping/BeginUpdateCart",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    const state = props.getState();
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    if (!cart) {
        throw new Error("There was no current cart available while trying to update the current cart.");
    }
    props.cart = cart;
    const oneTimeShipToAccount = props.parameter.oneTimeAddress
        ? JSON.stringify(props.parameter.oneTimeAddress)
        : undefined;
    props.apiParameter = {
        cart: { ...props.cart },
    };
    const properties = {
        ...props.apiParameter.cart.properties,
    };

    properties.oneTimeShipToAccount = oneTimeShipToAccount || "";

    if (!arePropertiesTheSame(props.apiParameter.cart.properties, properties)) {
        return false;
    }

    props.apiParameter.cart.properties = {
        ...properties,
    };
};

export const UpdateCart: HandlerType = async props => {
    props.apiResult = await updateCartApi(props.apiParameter);
};

export const LoadCart: HandlerType = props => {
    const state = props.getState();
    const { cartId } = state.pages.checkoutShipping;
    if (cartId) {
        props.dispatch(loadCart({ cartId }));
    } else {
        props.dispatch(loadCurrentCart({ getPromotions: true }));
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.();
};

export const DispatchCompleteUpdateCart: HandlerType = props => {
    props.dispatch({
        type: "Pages/CheckoutShipping/CompleteUpdateCart",
    });
};

export const chain = [
    DispatchBeginUpdateCart,
    PopulateApiParameter,
    UpdateCart,
    LoadCart,
    ExecuteOnSuccessCallback,
    DispatchCompleteUpdateCart,
];

const updateCartProperties = createHandlerChainRunnerOptionalParameter(chain, {}, "UpdateCartProperties");
export default updateCartProperties;
